import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const EditStudentForm = ({ student, classes, parents, editStudentMutation }) => {
  const { handleSubmit, control, setValue, watch } = useForm({
    defaultValues: {
      fullName: student.fullName,
      dateOfBirth: dayjs(student.dateOfBirth, "DD-MM-YYYY"),
      discount: student.discount,
      class: student.class ? student.class.id : "",
      gender: student.gender,
      isActive: student.isActive,
      previousSchool: student.previousSchool || "",
      curriculumType: student.curriculumType || "MIXED",
      gradeOfAdmission: student.gradeOfAdmission || "",
      parentID: student.parent ? student.parent.id : "",
    },
  });

  const selectedGender = watch("gender");

  // Filter classes by the student's gender
  const filteredClasses = classes
    ? classes.filter((cls) => !selectedGender || cls.gender === selectedGender)
    : [];

  const handleFormSubmission = (data) => {
    editStudentMutation.mutate(
      {
        ...data,
        dateOfBirth: data.dateOfBirth.format("DD-MM-YYYY"),
        previousSchool: data.previousSchool || null,
        curriculumType: data.curriculumType,
        gradeOfAdmission: data.gradeOfAdmission || null,
        parentID: data.parentID,
      },
      {
        onSuccess: () => {
          toast.success("Student updated successfully");
        },
        onError: () => {
          toast.error("Something went wrong");
        },
      }
    );
  };

  return (
    <div>
      <Typography variant="h5" component="h2" marginBottom={2}>
        Edit Student
      </Typography>
      <Box
        onSubmit={handleSubmit(handleFormSubmission)}
        component="form"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: 2,
        }}
        autoComplete="off"
      >
        <Controller
          render={({ field }) => (
            <TextField {...field} required label="Full name" />
          )}
          control={control}
          name="fullName"
        />
        <Controller
          render={({ field }) => (
            <TextField {...field} required label="Discount %" type="number" />
          )}
          control={control}
          name="discount"
        />
        <Controller
          render={({ field }) => (
            <DatePicker
              {...field}
              required
              label="Date of birth"
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          )}
          control={control}
          name="dateOfBirth"
        />
        <Controller
          render={({ field }) => (
            <FormControl fullWidth required {...field}>
              <InputLabel id="class-label">Grade</InputLabel>
              <Select
                onChange={(e) => setValue("class", e.target.value)}
                defaultValue={student.class ? student.class.id : ""}
                labelId="class-label"
                label="Grade"
              >
                {filteredClasses.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          control={control}
          name="class"
        />
        <Controller
          render={({ field }) => (
            <FormControl {...field}>
              <FormLabel id="gender-label">Gender</FormLabel>
              <RadioGroup
                defaultValue={student.gender}
                name="radio-buttons-group"
                onChange={(e) => {
                  field.onChange(e);
                  // Reset class selection when gender changes
                  setValue("class", "");
                }}
              >
                <FormControlLabel
                  value="MALE"
                  control={<Radio />}
                  label="Male"
                />
                <FormControlLabel
                  value="FEMALE"
                  control={<Radio />}
                  label="Female"
                />
              </RadioGroup>
            </FormControl>
          )}
          control={control}
          name="gender"
        />
        <Controller
          render={({ field }) => (
            <TextField {...field} label="Previous School" />
          )}
          control={control}
          name="previousSchool"
        />
        <Controller
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel id="curriculum-type-label">Curriculum Type</InputLabel>
              <Select
                {...field}
                labelId="curriculum-type-label"
                label="Curriculum Type"
              >
                <MenuItem value="LOCAL">Local</MenuItem>
                <MenuItem value="MIXED">Mixed</MenuItem>
                <MenuItem value="LONDON">London</MenuItem>
              </Select>
            </FormControl>
          )}
          control={control}
          name="curriculumType"
        />
        <Controller
          render={({ field }) => (
            <TextField {...field} label="Grade of Admission" />
          )}
          control={control}
          name="gradeOfAdmission"
        />
        <Controller
          render={({ field }) => (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Autocomplete
                options={parents}
                getOptionLabel={(parent) => parent.fullName}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.fullName}
                  </li>
                )}
                value={parents.find((p) => p.id === field.value) || null}
                onChange={(_, newValue) =>
                  field.onChange(newValue ? newValue.id : "")
                }
                renderInput={(params) => (
                  <TextField {...params} required label="Parent" />
                )}
                sx={{ flexGrow: 1 }}
              />
              {field.value && (
                <IconButton
                  component={Link}
                  to={`/parents/${field.value}`}
                  aria-label="Open parent details"
                  sx={{
                    alignSelf: "stretch",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    width: 56,
                  }}
                >
                  <OpenInNewIcon />
                </IconButton>
              )}
            </Box>
          )}
          control={control}
          name="parentID"
        />
        <Controller
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label="Active"
            />
          )}
          control={control}
          name="isActive"
        />
        <Button type="submit" variant="contained">
          Update Student
        </Button>
      </Box>
    </div>
  );
};

export default EditStudentForm;
